import { createContext, PropsWithChildren, useContext } from 'react';

export interface MenuLink {
	link_position: number;
	link_type: 'national_view' | 'state_view' | 'donor_gaps' | 'final_analysis';
	link_path: string | null;
	show_coming_soon_text: boolean;
	coming_soon_text: string;
}

export interface MenuLinksContextInterface {
	menuLinks: MenuLink[];
	landingPageLinks: MenuLink[];
	isChapterEnabled: (type: 'national_view' | 'state_view' | 'donor_gaps' | 'final_analysis') => boolean;
	getChapterLink: (type: string) => MenuLink | undefined;
	getChapterPosition: (type: string) => number;
}

const MenuLinksContext = createContext<MenuLinksContextInterface | undefined>(undefined);

type MenuLinksProviderProps = PropsWithChildren & {
	menuLinks?: MenuLink[];
	landingPageLinks?: MenuLink[];
};

export const MenuLinksProvider = ({
	children,
	menuLinks = [],
	landingPageLinks = []
}: MenuLinksProviderProps) => {
	const isChapterEnabled = (type: 'national_view' | 'state_view' | 'donor_gaps' | 'final_analysis'): boolean => {
		return menuLinks.some(link => link.link_type === type) ||
			landingPageLinks.some(link => link.link_type === type);
	};

	const getChapterLink = (type: string): MenuLink | undefined => {
		return menuLinks.find(link => link.link_type === type) ||
			landingPageLinks.find(link => link.link_type === type);
	};

	const getChapterPosition = (type: string): number => {
		const link = getChapterLink(type);
		return link?.link_position ?? 999; // Default to high number if not found
	};

	const value: MenuLinksContextInterface = {
		menuLinks,
		landingPageLinks,
		isChapterEnabled,
		getChapterLink,
		getChapterPosition
	};

	return (
		<MenuLinksContext.Provider value={value}>
			{children}
		</MenuLinksContext.Provider>
	);
};

export const useMenuLinks = (): MenuLinksContextInterface => {
	const context = useContext(MenuLinksContext);
	if (context === undefined) {
		throw new Error('useMenuLinks must be used within a MenuLinksProvider');
	}
	return context;
};